// src/components/certificacion/CertificacionDetalle.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, LinearProgress, Divider, Chip,
  Button, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, CircularProgress, Tooltip, Alert,
} from '@mui/material';
import { EditOutlined, RefreshOutlined } from '@mui/icons-material';
import { certificacionService } from '../../services/certificacionService';
import CertificacionPaso from './CertificacionPaso';

const ESTADO_COLOR = {
  en_progreso: 'info',
  completado:  'success',
  suspendido:  'error',
};

const AMBIENTE_LABEL = {
  certificacion: 'Certificación',
  produccion:    'Producción',
};

export default function CertificacionDetalle({ rutEmpresa }) {
  const [estado, setEstado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogNotas, setDialogNotas] = useState(false);
  const [notasEdit, setNotasEdit] = useState('');
  const [savingNotas, setSavingNotas] = useState(false);
  const pollingRef = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const data = await certificacionService.getEstado(rutEmpresa);
      setEstado(data);
      setError(null);
    } catch (err) {
      setError(err?.message || 'Error al cargar certificación');
    } finally {
      setLoading(false);
    }
  }, [rutEmpresa]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Polling cuando hay algún paso en_proceso
  useEffect(() => {
    const hayEnProceso = estado?.pasos?.some(p => p.estado === 'en_proceso');
    if (hayEnProceso) {
      pollingRef.current = setInterval(cargar, 10000);
    }
    return () => clearInterval(pollingRef.current);
  }, [estado, cargar]);

  const handleAbrirNotas = () => {
    setNotasEdit(estado?.notas || '');
    setDialogNotas(true);
  };

  const handleGuardarNotas = async () => {
    setSavingNotas(true);
    try {
      await certificacionService.actualizarNotas(rutEmpresa, notasEdit);
      setDialogNotas(false);
      cargar();
    } catch (err) {
      console.error('Error guardando notas:', err);
    } finally {
      setSavingNotas(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!estado) return null;

  const { progreso, pasos } = estado;
  const pasoSetPruebas = pasos.find(p => p.codigo === 'cargar_set_pruebas');
  const setNoCargado = pasoSetPruebas && pasoSetPruebas.estado !== 'completado';

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      {/* Cabecera */}
      <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {estado.rut_empresa}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip
                label={estado.tipo_onboarding.charAt(0).toUpperCase() + estado.tipo_onboarding.slice(1)}
                size="small"
                variant="outlined"
              />
              <Chip
                label={AMBIENTE_LABEL[estado.ambiente] || estado.ambiente}
                size="small"
                color="info"
                variant="outlined"
              />
              <Chip
                label={estado.estado}
                size="small"
                color={ESTADO_COLOR[estado.estado] || 'default'}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Actualizar">
              <IconButton size="small" onClick={cargar}>
                <RefreshOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Editar notas">
              <IconButton size="small" onClick={handleAbrirNotas}>
                <EditOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Barra de progreso */}
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Progreso
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {progreso.completados}/{progreso.total} ({progreso.porcentaje}%)
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progreso.porcentaje}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      </Box>

      <Divider />

      {/* Banner set de pruebas pendiente */}
      {setNoCargado && (
        <Alert severity="info" sx={{ m: 1, mb: 0 }}>
          Para avanzar en la certificación, descarga el archivo <strong>.txt</strong> desde
          el portal SII y súbelo en el paso "{pasoSetPruebas.nombre}".
        </Alert>
      )}

      {/* Lista de pasos */}
      <Box sx={{ p: 1 }}>
        {pasos.map((paso, idx) => {
          // Para pasos de envío (set_basico, set_tipo, etc.) pasar el paso resultado asociado
          // así el componente puede mostrar el botón re-enviar por caso según el resultado del SII
          let pasosResultado = null;
          let pasoSet = null;
          if (paso.codigo?.startsWith('set_')) {
            const codigoResultado = paso.codigo.replace('set_', 'resultado_set_');
            pasosResultado = pasos.find(p => p.codigo === codigoResultado) || null;
          }
          if (paso.codigo?.startsWith('resultado_set_')) {
            const codigoSet = paso.codigo.replace('resultado_set_', 'set_');
            pasoSet = pasos.find(p => p.codigo === codigoSet) || null;
          }
          return (
            <React.Fragment key={paso.id}>
              <CertificacionPaso
                paso={paso}
                rutEmpresa={rutEmpresa}
                onActualizado={cargar}
                pasoResultado={pasosResultado}
                pasoSet={pasoSet}
              />
              {idx < pasos.length - 1 && <Divider sx={{ my: 0.5 }} />}
            </React.Fragment>
          );
        })}
      </Box>

      {estado.notas && (
        <>
          <Divider />
          <Box sx={{ p: 1.5, bgcolor: 'grey.50' }}>
            <Typography variant="caption" color="text.secondary">Notas: </Typography>
            <Typography variant="caption">{estado.notas}</Typography>
          </Box>
        </>
      )}

      {/* Dialog editar notas */}
      <Dialog open={dialogNotas} onClose={() => setDialogNotas(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Notas de certificación</DialogTitle>
        <DialogContent>
          <TextField
            value={notasEdit}
            onChange={e => setNotasEdit(e.target.value)}
            multiline
            rows={4}
            fullWidth
            size="small"
            placeholder="Observaciones del proceso..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogNotas(false)} disabled={savingNotas}>Cancelar</Button>
          <Button onClick={handleGuardarNotas} variant="contained" disabled={savingNotas}>
            {savingNotas ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
